import React, { useMemo, useState, useCallback } from 'react';
import '../../styles/Auction.css';
import DashboardLayout from '../../components/layout/DashboardLayout';
import AuctionSessionList from '../../components/specific/auction/AuctionSessionList';
import AuctionFilters from '../../components/specific/auction/AuctionFilters';
import auctionService from '../../services/auctionService';
import { useInfiniteQuery } from '@tanstack/react-query';
import Button from '../../components/common/Button';
import { useToast } from '../../hooks/useToast';

function EditSessionModal({ session, onClose, onSaved }) {
    const [form, setForm] = useState({
        starting_price: session?.starting_price || '',
        duration_minutes: session?.duration_minutes || '',
    });
    const [saving, setSaving] = useState(false);
    const { showToast } = useToast();

    if (!session) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await auctionService.updateAuctionSession(session.session_id, {
                starting_price: Number(form.starting_price),
                duration_minutes: Number(form.duration_minutes),
            });
            showToast('Session updated', 'success');
            onSaved();
            onClose();
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to update session', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
                <h3 style={{ marginTop: 0 }}>Edit Session — {session.token_symbol}</h3>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                        <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Starting Price (Rs)</label>
                        <input required type="number" min="1" value={form.starting_price}
                            onChange={(e) => setForm(f => ({ ...f, starting_price: e.target.value }))}
                            style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db' }} />
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Duration (minutes)</label>
                        <input required type="number" min="1" value={form.duration_minutes}
                            onChange={(e) => setForm(f => ({ ...f, duration_minutes: e.target.value }))}
                            style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db' }} />
                    </div>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                        <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
                        <Button variant="primary" type="submit" disabled={saving}>
                            {saving ? 'Saving...' : 'Update'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function LiveBidSessions() {
    const [statusFilter, setStatusFilter] = useState('active');
    const [editSession, setEditSession] = useState(null);
    const { showToast } = useToast();

    const sessionsQuery = useInfiniteQuery({
        queryKey: ['manager-live-sessions', statusFilter],
        initialPageParam: undefined,
        queryFn: ({ pageParam }) => auctionService.listSessions(statusFilter, { cursor: pageParam, limit: 20 }),
        getNextPageParam: (lastPage) => {
            const pagination = lastPage?.data?.pagination;
            return pagination?.has_more ? pagination?.next_cursor : undefined;
        },
    });

    const sessions = useMemo(() => (
        sessionsQuery.data?.pages?.flatMap((page) => Array.isArray(page?.data?.sessions) ? page.data.sessions : []) || []
    ), [sessionsQuery.data]);

    const handleDelete = useCallback(async (session) => {
        if (!window.confirm(`Delete session for ${session.token_symbol}? This cannot be undone.`)) return;
        try {
            await auctionService.deleteAuctionSession(session.session_id);
            showToast('Session deleted', 'success');
            sessionsQuery.refetch();
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to delete session', 'error');
        }
    }, [showToast, sessionsQuery]);

    return (
        <DashboardLayout>
            <div style={{ padding: '1rem' }}>
                <h2 style={{ marginBottom: '1.5rem' }}>Live Auction Sessions</h2>

                <AuctionFilters
                    value={statusFilter}
                    onChange={setStatusFilter}
                />

                {sessionsQuery.isLoading ? (
                    <p>Loading sessions...</p>
                ) : (
                    <>
                        <AuctionSessionList
                            sessions={sessions}
                            onEdit={(s) => setEditSession(s)}
                            onDelete={handleDelete}
                        />
                        {sessionsQuery.hasNextPage ? (
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                                <Button
                                    variant="secondary"
                                    onClick={() => sessionsQuery.fetchNextPage()}
                                    disabled={sessionsQuery.isFetchingNextPage}
                                >
                                    {sessionsQuery.isFetchingNextPage ? 'Loading...' : 'Load More Sessions'}
                                </Button>
                            </div>
                        ) : null}
                    </>
                )}
            </div>

            <EditSessionModal
                session={editSession}
                onClose={() => setEditSession(null)}
                onSaved={() => sessionsQuery.refetch()}
            />
        </DashboardLayout>
    );
}
